import React, { useState, useEffect } from "react";
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon as CheckCircleIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
} from "@heroicons/react/24/solid";

const SubmitConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  totalQuestions,
  answeredQuestions,
  isSubmitting = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return; // Không cho phép đóng khi đang submit
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleConfirm = () => {
    onConfirm();
  };

  if (!isOpen && !isVisible) return null;

  const progressPercentage = (answeredQuestions / totalQuestions) * 100;
  const unansweredQuestions = totalQuestions - answeredQuestions;

  return (
    <>
      {/* Backdrop with modern blur effect */}
      <div
        className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/40 to-purple-900/60 backdrop-blur-lg z-[9999] transition-all duration-500 ${
          isVisible ? "opacity-100" : "opacity-0"
        } ${isSubmitting ? "cursor-not-allowed" : "cursor-pointer"}`}
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div
        className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 transition-all duration-500 ${
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"
        }`}
      >
        <div className="relative w-full max-w-lg bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden transform transition-all duration-500">
          {/* Close Button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-all duration-200 ${
              isSubmitting
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white/80 hover:bg-white text-gray-600 hover:text-gray-800 shadow-lg hover:shadow-xl hover:scale-110"
            }`}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-emerald-400/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          {/* Header Section */}
          <div className="relative px-8 pt-12 pb-6 text-center">
            {/* Warning Icon */}
            <div className="relative inline-flex items-center justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl animate-pulse">
                <ExclamationTriangleIconSolid className="w-10 h-10 text-white" />
              </div>
              {/* Floating rings */}
              <div className="absolute inset-0 rounded-full border-4 border-amber-300/30 animate-ping"></div>
              <div className="absolute inset-0 rounded-full border-2 border-orange-400/50 animate-pulse delay-500"></div>
            </div>

            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
              Xác nhận nộp bài
            </h2>
            <p className="text-gray-600 text-sm">
              Bạn có chắc chắn muốn kết thúc bài thi không?
            </p>
          </div>

          {/* Progress Section */}
          <div className="px-8 pb-6">
            {/* Progress Stats Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Answered Questions Card */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-4 border border-emerald-100">
                <div className="flex items-center mb-2">
                  <CheckCircleIconSolid className="w-5 h-5 text-emerald-600 mr-2" />
                  <span className="text-sm font-medium text-emerald-700">
                    Đã làm
                  </span>
                </div>
                <div className="text-2xl font-bold text-emerald-800">
                  {answeredQuestions}
                </div>
                <div className="text-xs text-emerald-600">
                  {progressPercentage.toFixed(0)}% hoàn thành
                </div>
              </div>

              {/* Unanswered Questions Card */}
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-4 border border-red-100">
                <div className="flex items-center mb-2">
                  <ClockIcon className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-sm font-medium text-red-700">
                    Chưa làm
                  </span>
                </div>
                <div className="text-2xl font-bold text-red-800">
                  {unansweredQuestions}
                </div>
                <div className="text-xs text-red-600">
                  {(100 - progressPercentage).toFixed(0)}% còn lại
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {/* <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Tiến độ làm bài
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {progressPercentage.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-green-600 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                  style={{ width: `${progressPercentage}%` }}
                >
                  <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                </div>
              </div>
            </div> */}

            {/* Warning Message */}
            {/* {unansweredQuestions > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-4 mb-6">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 mb-1">
                      Lưu ý quan trọng
                    </p>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      Bạn vẫn có{" "}
                      <span className="font-semibold">
                        {unansweredQuestions} câu chưa trả lời
                      </span>
                      . Những câu này sẽ được tính là sai. Bạn vẫn còn thời gian
                      để hoàn thiện bài thi.
                    </p>
                  </div>
                </div>
              </div>
            )} */}
          </div>

          {/* Action Buttons */}
          <div className="px-8 pb-8 space-y-3">
            {/* Continue Button */}
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className={`w-full font-bold py-4 px-6 rounded-2xl transition-all duration-300 transform flex items-center justify-center group ${
                isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              }`}
            >
              <ClockIcon className="w-5 h-5 mr-2 group-hover:animate-spin" />
              <span>Tiếp tục làm bài</span>
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Submit Button */}
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className={`w-full font-bold py-4 px-6 rounded-2xl transition-all duration-300 transform flex items-center justify-center group relative overflow-hidden ${
                isSubmitting
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              } text-white`}
            >
              {/* Animated background for submit button */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-400/50 to-pink-400/50 transform -skew-x-12 translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>

              <div className="relative flex items-center">
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                    <span>Đang nộp bài...</span>
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="w-5 h-5 mr-2" />
                    <span>Nộp bài thi</span>
                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitConfirmationModal;
